import React, { createContext, useContext, useEffect, useReducer } from 'react';

import useAsync from 'components/hooks/useAsync';

import {
  ENGAGEMENT_ADMIN,
  ENGAGEMENT_ADMIN_LABEL,
  FEEDBACK_ADMIN,
  FEEDBACK_ADMIN_LABEL,
  GOAL_ADMIN,
  GOAL_ADMIN_LABEL,
  ONE_ON_ONE_ADMIN,
  ONE_ON_ONE_ADMIN_LABEL,
  PLATFORM_ADMIN,
  PLATFORM_ADMIN_LABEL,
  REVIEW_ADMIN,
  REVIEW_ADMIN_LABEL,
  TASK_ADMIN,
  TASK_ADMIN_LABEL,
} from 'constants/ProjectConstants';

import { getCustomDefinitionList } from 'services/customDefinition';

export const TABLE_ROW_LIMIT = 10;

export const CUSTOM_ROLE_VIEWS = {
  default: 'home',
  createRole: 'createRole',
  editRole: 'editRole',
};

export const CUSTOM_ROLE_ACTION_TYPES = {
  initializeState: 'initializeState',
  // views
  openDefaultView: 'openDefaultView',
  openCreateNewView: 'openCreateNewView',
  openEditView: 'openEditView',
  cancelRoleEdit: 'cancelRoleEdit',
  // table data view
  openNextPage: 'openNextPage',
  openPreviousPage: 'openPreviousPage',
  // data updation
  createNewRole: 'createNewRole',
  updateRole: 'updateRole',
  deleteRole: 'deleteRole',
  addEmployeesToRole: 'addEmployeesToRole',
  deleteEmployeesFromRole: 'deleteEmployeesFromRole',
  // employee edit sidepanel
  openEmployeeEditSidePanel: 'openEmployeeEditSidePanel',
  closeEmployeeEditSidePanel: 'closeEmployeeEditSidePanel',
};

const customRoleInitialState = {
  allRoles: [],
  page: 0,
  hasMore: false,
  // routing
  currentView: CUSTOM_ROLE_VIEWS.default,
  editingRoleId: null,
  // add delete employee sidepanel,
  isEmployeeSidePanelOpen: false,
  customRoleTableRowObj: null,
};

const SORT_ORDER_ADMIN_ROLES = [
  PLATFORM_ADMIN_LABEL,
  GOAL_ADMIN_LABEL,
  ONE_ON_ONE_ADMIN_LABEL,
  FEEDBACK_ADMIN_LABEL,
  REVIEW_ADMIN_LABEL,
  ENGAGEMENT_ADMIN_LABEL,
  TASK_ADMIN_LABEL,
];

const sortAdminRoles = (a, b) => {
  const nameAIndex = SORT_ORDER_ADMIN_ROLES.indexOf(a.customRole.name);
  const nameBIndex = SORT_ORDER_ADMIN_ROLES.indexOf(b.customRole.name);

  if (nameBIndex === -1) {
    if (nameAIndex === -1) return 0;

    return -1;
  }
  if (nameAIndex === -1) return 1;

  return nameAIndex < nameBIndex ? -1 : 1;
};

const sortRoles = (a, b) => {
  const nameA = a.customRole.name.toLowerCase();
  const nameB = b.customRole.name.toLowerCase();

  if (nameA < nameB) return -1;
  if (nameA > nameB) return 1;
  return 0;
};

const customRoleStateReducer = (state, action) => {
  switch (action.type) {
    case CUSTOM_ROLE_ACTION_TYPES.initializeState: {
      const sortedAdminRoles = action.payload.allRoles.filter((role) => !role.custom).sort(sortAdminRoles);

      const sortedCustomRoles = action.payload.allRoles.filter((role) => role.custom).sort(sortRoles);

      const sortedAllRoles = [...sortedAdminRoles, ...sortedCustomRoles];
      return {
        ...state,
        allRoles: sortedAllRoles,
        hasMore: action.payload.hasMore,
      };
    }
    case CUSTOM_ROLE_ACTION_TYPES.openDefaultView: {
      return {
        ...state,
        currentView: CUSTOM_ROLE_VIEWS.default,
        editingRoleId: null,
      };
    }
    case CUSTOM_ROLE_ACTION_TYPES.openCreateNewView: {
      return {
        ...state,
        currentView: CUSTOM_ROLE_VIEWS.createRole,
      };
    }
    case CUSTOM_ROLE_ACTION_TYPES.openEditView: {
      return {
        ...state,
        currentView: CUSTOM_ROLE_VIEWS.editRole,
        editingRoleId: action.payload.id,
      };
    }
    case CUSTOM_ROLE_ACTION_TYPES.openNextPage: {
      return {
        ...state,
        page: state.page + 1,
        hasMore: state.allRoles.length > (state.page + 2) * TABLE_ROW_LIMIT,
      };
    }
    case CUSTOM_ROLE_ACTION_TYPES.openPreviousPage: {
      return {
        ...state,
        page: state.page - 1,
        hasMore: state.allRoles.length > state.page * TABLE_ROW_LIMIT,
      };
    }
    case CUSTOM_ROLE_ACTION_TYPES.createNewRole: {
      const sortedAdminRoles = state.allRoles.filter((role) => !role.custom).sort(sortAdminRoles);

      const sortedCustomRoles = [
        ...state.allRoles.filter((role) => role.custom),
        {
          customRole: {
            id: action.payload.id,
            name: action.payload.name,
          },
          employees: [],
          custom: true,
        },
      ].sort(sortRoles);

      const sortedAllRoles = [...sortedAdminRoles, ...sortedCustomRoles];

      return {
        ...state,
        allRoles: sortedAllRoles,
        hasMore: state.allRoles.length + 1 > (state.page + 1) * TABLE_ROW_LIMIT,
        currentView: CUSTOM_ROLE_VIEWS.default,
      };
    }
    case CUSTOM_ROLE_ACTION_TYPES.updateRole: {
      const sortedAdminRoles = state.allRoles.filter((role) => !role.custom).sort(sortAdminRoles);

      const customRoles = state.allRoles.filter((role) => role.custom).sort(sortRoles);
      const matchedRoleIndex = customRoles.findIndex(({ customRole }) => customRole.id === action.payload.id);

      const sortedCustomRoles = [
        ...customRoles.slice(0, matchedRoleIndex),
        {
          customRole: {
            id: action.payload.id,
            name: action.payload.name,
          },
          employees: customRoles[matchedRoleIndex].employees,
          custom: true,
        },
        ...customRoles.slice(matchedRoleIndex + 1, customRoles.length),
      ].sort(sortRoles);

      const sortedAllRoles = [...sortedAdminRoles, ...sortedCustomRoles];

      return {
        ...state,
        allRoles: sortedAllRoles,
        currentView: CUSTOM_ROLE_VIEWS.default,
        editingRoleId: null,
      };
    }
    case CUSTOM_ROLE_ACTION_TYPES.deleteRole: {
      return {
        ...state,
        allRoles: state.allRoles.filter(({ customRole }) => customRole.id !== action.payload.id),
        hasMore: state.allRoles.length - 1 > (state.page + 1) * TABLE_ROW_LIMIT,
      };
    }
    case CUSTOM_ROLE_ACTION_TYPES.openEmployeeEditSidePanel: {
      return {
        ...state,
        isEmployeeSidePanelOpen: true,
        customRoleTableRowObj: action.payload.tableRowObj,
      };
    }
    case CUSTOM_ROLE_ACTION_TYPES.closeEmployeeEditSidePanel: {
      return {
        ...state,
        isEmployeeSidePanelOpen: false,
        customRoleTableRowObj: null,
        allRoles: state.allRoles.map((role) => {
          if (role.customRole.id === state.customRoleTableRowObj.customRole.id) {
            return {
              ...role,
              employees: state.customRoleTableRowObj.employees,
            };
          }
          return role;
        }),
      };
    }
    case CUSTOM_ROLE_ACTION_TYPES.addEmployeesToRole: {
      return {
        ...state,
        customRoleTableRowObj: {
          ...state.customRoleTableRowObj,
          employees: [...state.customRoleTableRowObj.employees, action.payload.employee],
        },
      };
    }
    case CUSTOM_ROLE_ACTION_TYPES.deleteEmployeesFromRole: {
      return {
        ...state,
        customRoleTableRowObj: {
          ...state.customRoleTableRowObj,
          employees: state.customRoleTableRowObj.employees.filter((employee) => employee.id !== action.payload.id),
        },
      };
    }

    default:
      return state;
  }
};

export const CustomRoleContext = createContext();

export const useCustomRoleContext = () => useContext(CustomRoleContext);

const renameAdminAccesses = (access) => {
  switch (access) {
    case PLATFORM_ADMIN:
      return PLATFORM_ADMIN_LABEL;
    case GOAL_ADMIN:
      return GOAL_ADMIN_LABEL;
    case ONE_ON_ONE_ADMIN:
      return ONE_ON_ONE_ADMIN_LABEL;
    case REVIEW_ADMIN:
      return REVIEW_ADMIN_LABEL;
    case ENGAGEMENT_ADMIN:
      return ENGAGEMENT_ADMIN_LABEL;
    case FEEDBACK_ADMIN:
      return FEEDBACK_ADMIN_LABEL;
    case TASK_ADMIN:
      return TASK_ADMIN_LABEL;
    default:
      return access;
  }
};

export const CustomRoleContextProvider = ({ children }) => {
  const [state, customRoleDispatch] = useReducer(customRoleStateReducer, customRoleInitialState);

  const { run, isLoading, isError } = useAsync();

  const visibleRoles = state.allRoles.slice(state.page * TABLE_ROW_LIMIT, (state.page + 1) * TABLE_ROW_LIMIT);

  const fetchData = ({ offset, limit }) =>
    getCustomDefinitionList({
      offset,
      limit,
      isAdminRoleRequired: true,
    }).then((res) => {
      // rename admin accesses.

      const renamedRoles = res.map((role) => {
        if (!role.custom) {
          return {
            ...role,
            customRole: { ...role?.customRole, name: renameAdminAccesses(role?.customRole?.name) },
          };
        }
        return role;
      });

      customRoleDispatch({
        type: CUSTOM_ROLE_ACTION_TYPES.initializeState,
        payload: {
          allRoles: renamedRoles,
          hasMore: res.length > TABLE_ROW_LIMIT,
        },
      });
    });

  const handlePageChange = (ev, newPage) => {
    if (newPage === state.page + 1) {
      customRoleDispatch({
        type: CUSTOM_ROLE_ACTION_TYPES.openNextPage,
        payload: null,
      });
    } else {
      customRoleDispatch({
        type: CUSTOM_ROLE_ACTION_TYPES.openPreviousPage,
        payload: null,
      });
    }
  };

  useEffect(() => {
    const initialOffset = 0;
    const initialLimit = 1000;
    run(
      fetchData({
        offset: initialOffset,
        limit: initialLimit,
      })
    );
  }, []);

  return (
    <CustomRoleContext.Provider
      value={{
        state: {
          ...state,
          isLoading,
          isError,
          visibleData: visibleRoles,
        },
        handlePageChange,
        customRoleDispatch,
      }}
    >
      {children}
    </CustomRoleContext.Provider>
  );
};
